
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import { styled } from "@mui/material/styles";
import { Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Grow from "@mui/material/Grow";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SocialCard from "examples/Cards/SocialCard";
import GuidanceBubble from "layouts/guidance/components/GuidanceBubble";

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 400,
    textAlign: "left",
  },
});

const tooltipContent = (
  <div style={{ maxWidth: "350px", textAlign: "left" }}>
    <Typography variant="body2" gutterBottom>
      <strong>How to Use Social Stash:</strong>
    </Typography>
    <Typography variant="caption" display="block" gutterBottom>
      - Store and manage saved <strong>facebook</strong> profiles. <br />
      - Select profiles to analyse profiles. <br />
      - Selete profiles to delete if they are not needed. <br />
    </Typography>

    <Typography variant="body2" gutterBottom>
      <strong>Quick Actions:</strong>
    </Typography>
    <Typography variant="caption" display="block" gutterBottom>
      - Use the <strong>search bar</strong> to quickly find profiles. <br />
      - Click <strong>Add Profile</strong> to save new profiles. <br />
    </Typography>

    <Typography variant="body2" gutterBottom>
      <strong>Generating Reports:</strong>
    </Typography>
    <Typography variant="caption" display="block">
      - Select one or more profiles. <br />
      - Click the <strong>Analyse Profile</strong> button. <br />
      - Cross platform reporting is also available. <br />
    </Typography>
  </div>
);

const FacebookSocialCard = ({
  profiles,
  loading = false,
  error = null,
  formatCount,
  selectedProfiles,
  onProfileSelect,
  demoMode = false, // [NEW]
  demoTooltip = "", // [NEW]
}) => {
  const isSelected = (profileId) => {
    return selectedProfiles.some(
      (selected) => selected.id === profileId && selected.platform === "Facebook"
    );
  };

  return (
    <Card>
      <MDBox>
        <MDBox
          mx={2}
          mt={-3}
          py={1}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          display="flex"
          flexDirection="row"
        >
          <MDTypography variant="h6" color="white">
            Facebook
          </MDTypography>
          <CustomTooltip
            title={tooltipContent}
            placement="bottom"
            arrow
            PopperProps={{
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 10],
                  },
                },
              ],
            }}
          >
            <IconButton sx={{ color: "white", p: 0, ml: 0.5 }}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </CustomTooltip>
        </MDBox>

        <MDBox pt={1} pb={3} px={2}>
          {loading ? (
            <MDBox display="flex" justifyContent="center" alignItems="center" p={3}>
              <CircularProgress color="accent" />
            </MDBox>
          ) : error ? (
            <MDTypography color="error" align="center">
              {error}
            </MDTypography>
          ) : profiles.length > 0 ? (
            <MDBox
              display="flex"
              flexWrap="wrap"
              justifyContent="center"
              gap={2}
              px={2}
              mt={2}
            >
              {profiles.map((profile, index) => {
                // 🔵 DEMO: Highlight the FIRST card only
                const isDemoHighlight = demoMode && index === 0;

                return (
                  <Grow
                    in={true}
                    timeout={index * 50}
                    key={profile.id}
                    style={{ transformOrigin: "0 0 0" }}
                  >
                    <MDBox
                      sx={{
                        flex: "1 1 calc(20% - 16px)",
                        minWidth: "240px",
                        maxWidth: "300px",
                        position: isDemoHighlight ? "relative" : "initial",
                        zIndex: isDemoHighlight ? 1350 : "auto", // Above backdrop (1250)
                        transform: isDemoHighlight ? "scale(1.05)" : "none",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      {isDemoHighlight && (
                        <GuidanceBubble
                          visible={true}
                          message={demoTooltip || "Click here for Demo"}
                        />
                      )}

                      <SocialCard
                        platform="Facebook"
                        profilePic={profile.profilePictureUrl}
                        name={profile.name}
                        count={profile.count}
                        link={profile.link}
                        verifiedStatus={profile.verifiedStatus}
                        updated_at={profile.updated_at}
                        isSelected={isDemoHighlight ? false : isSelected(profile.id)} // Visually unselected initially, user must click
                        isOwn={profile.own}
                        onSelect={() =>
                          onProfileSelect(
                            profile.id,
                            profile.name,
                            profile.profilePictureUrl,
                            profile.link,
                            "Facebook"
                          )
                        }
                      />
                    </MDBox>
                  </Grow>
                );
              })}
            </MDBox>
          ) : (
            <MDTypography variant="subtitle1" color="text" align="center">
              No profiles found.
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
    </Card>
  );
};

FacebookSocialCard.propTypes = {
  profiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      profilePictureUrl: PropTypes.string,
      name: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      verifiedStatus: PropTypes.bool,
    })
  ).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  formatCount: PropTypes.func.isRequired,
  selectedProfiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      platform: PropTypes.string.isRequired,
    })
  ).isRequired,
  onProfileSelect: PropTypes.func.isRequired,
  demoMode: PropTypes.bool,
  demoTooltip: PropTypes.string,
};

// FacebookSocialCard.defaultProps = {
//   loading: false,
//   error: null,
// };

export default FacebookSocialCard;
